import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppNotification, NotificationStats, NotificationWebSocketMessage } from '../types';
import api from '../services/api';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [stats, setStats] = useState<NotificationStats | null>(null);

  const { token, user } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (unreadOnly = false) => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await api.get('/notifications', {
        params: {
          limit: 50,
          offset: 0,
          unread_only: unreadOnly
        }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;

    try {
      const response = await api.get('/notifications/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  // Fetch notification stats
  const fetchStats = useCallback(async () => {
    if (!token) return;

    try {
      const response = await api.get('/notifications/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching notification stats:', error);
    }
  }, [token]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!token) return false;

    try {
      await api.post(`/notifications/${notificationId}/mark-read`);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));

      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }, [token]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!token) return 0;

    try {
      const response = await api.post('/notifications/mark-all-read');
      const markedCount = response.data.marked_read;

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );

      setUnreadCount(0);
      return markedCount;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return 0;
    }
  }, [token]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!token) return false;

    try {
      await api.delete(`/notifications/${notificationId}`);

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Update unread count if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }, [token, notifications]);

  // Refresh all notification data
  const refreshNotifications = useCallback(() => {
    fetchNotifications();
    fetchUnreadCount();
    fetchStats();
  }, [fetchNotifications, fetchUnreadCount, fetchStats]);

  // WebSocket connection management
  const connectWebSocket = useCallback(() => {
    if (!token || !user?.id) {
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      // Connect to the backend WebSocket endpoint
      const wsBaseUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws';
      const wsUrl = `${wsBaseUrl}/notifications?token=${encodeURIComponent(token)}`;

      // WebSocket connection attempt

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;

        // Send initial message to get unread count
        ws.send(JSON.stringify({
          type: 'get_unread_count',
          timestamp: new Date().toISOString()
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message: NotificationWebSocketMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'notification':
              // New notification received
              const newNotification = message.data;
              setNotifications(prev => [newNotification, ...prev]);
              setUnreadCount(prev => prev + 1);

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                new Notification(newNotification.title, {
                  body: newNotification.message,
                  icon: '/favicon.ico',
                  badge: '/favicon.ico'
                });
              }
              break;

            case 'unread_count':
              setUnreadCount(message.data.count);
              break;

            case 'notification_marked_read':
              // Update local state when notification marked read elsewhere
              const readNotificationId = message.data.notification_id;
              setNotifications(prev =>
                prev.map(n =>
                  n.id === readNotificationId
                    ? { ...n, is_read: true, read_at: new Date().toISOString() }
                    : n
                )
              );
              break;

            case 'connection_established':
              // Connection confirmed
              break;

            case 'ping':
              // Respond to server ping with pong
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                  type: 'pong',
                  timestamp: message.data.timestamp
                }));
              }
              break;

            case 'pong':
              // Heartbeat response received
              break;

            default:
              // Unknown message type received
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = (event) => {
        setConnected(false);

        // Attempt to reconnect unless it was a manual close
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connectWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket connection failed');
        setConnected(false);
      };

      // Set up heartbeat to keep connection alive
      const heartbeat = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'ping',
            timestamp: new Date().toISOString()
          }));
        }
      }, 45000); // Send ping every 45 seconds (reasonable for most networks)

      // Clean up heartbeat when connection closes
      ws.addEventListener('close', () => {
        clearInterval(heartbeat);
      });

    } catch (error) {
      console.error('Failed to establish WebSocket connection');
      setConnected(false);
    }
  }, [token, user?.id]);

  // Initialize notifications on mount
  useEffect(() => {
    if (token) {
      refreshNotifications();
      connectWebSocket();

      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }

    return () => {
      // Clean up WebSocket connection
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, connectWebSocket, refreshNotifications]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    connected,
    stats,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    fetchNotifications,
    fetchUnreadCount,
    fetchStats
  };
};